import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes as RoutesWrapper,
} from "react-router-dom";
import { ForgotPassword } from "./authentication/ForgotPassword/ForgotPassword";
import { LoadingPage } from "./authentication/LoadingPage/LoadingPage";
import { Login } from "./authentication/Login/Login";
import { NewPassword } from "./authentication/NewPassword/NewPassword";
import { GuestRoute } from "./authentication/components/GuestRoute";
import { PortalPageVisibilityGuard } from "./authentication/components/PortalPageVisibilityGuard";
import { ProtectedRoute } from "./authentication/components/ProtectedRoute";
import Home from "./pages/Home/Home";
import { AssignmentsView } from "./pages/Home/components/AssignmentsView/AssignmentsView";
import { Courses } from "./pages/Home/UserAssignments";
import { PortalConfiguration } from "./pages/PortalConfiguration/PortalConfiguration";
import { PortalDesign } from "./pages/PortalConfiguration/PortalDesign/PortalDesign";
import { PortalPages } from "./pages/PortalConfiguration/PortalPages/PortalPages";
import { PortalSettings } from "./pages/PortalConfiguration/PortalSettings/PortalSettings";
import { FindAndReplace } from "./pages/PortalConfiguration/PortalTranslation/FindAndReplace/FindAndReplace";
import { PortalTranslation } from "./pages/PortalConfiguration/PortalTranslation/PortalTranslation";
import { UserAttributes } from "./pages/PortalConfiguration/UserAttributes/UserAttributes";
import { PortalGroups } from "./pages/PortalConfiguration/PortalGroups/PortalGroups";
import GlobalErrorHandler from "./components/GlobalErrorHandler/GlobalErrorHandler";
import { AssignmentManagement } from "./pages/Assignments/AssignmentManagement/AssignmentManagement";
import { AssignmentUploads } from "./pages/Assignments/AssignmentUploads/AssignmentUploads";
import { AssignmentRules } from "./pages/Assignments/AssignmentRules/AssignmentRules";
import { AddEditRule } from "./pages/Assignments/AssignmentRules/AddEditRule/AddEditRule";
import EmailSend from "./pages/EmailManagement/EmailSend/EmailSend";
import EmailTemplateEditor from "./pages/EmailManagement/EmailTemplateEditor/EmailTemplateEditor";
import HomeAssignment from "./pages/HomeAssignments/HomeAssignment";
import { EditAttributes } from "./pages/PortalConfiguration/Users/EditAttributes/EditAttributes";
import EmailTemplates from "./pages/EmailManagement/EmailTemplates/EmailTemplates";
import ManageAssignments from "./pages/Assignments/ManageAssignments/ManageAssignments";
import AddAssignment from "./pages/Assignments/AddAssignment/AddAssignment";
import EditAssignment from "./pages/Assignments/EditAssignment/EditAssignment";
import { AddNewUser } from "./pages/UserManagement/AddNewUser/AddNewUser";
import { Users } from "./pages/UserManagement/Users/Users";
import { Reports } from "./pages/Analytics/Reports/Reports";
import { Dashboard } from "./pages/Analytics/Dashboard/Dashboard";
import { NewReportTemplate } from "./pages/Analytics/NewReportTemplate/NewReportTemplate";
import { UserReport } from "./pages/Analytics/RunReports/UserReport/UserReport";
import { AssignmentActivityReport } from "./pages/Analytics/RunReports/AssignmentActivityReport/AssignmentActivityReport";
import { Certificates } from "./pages/Certificates/Certificates";
import MyProfile from "./pages/MyProfile/MyProfile";
import { UserActivityReport } from "./pages/Analytics/RunReports/UserActivityReport/UserActivityReport";
import { FileHub } from "./pages/FileHub/FileHub";
import { Roles } from "./services/api/types/users/users";
import { AppNotFoundRoute } from "./components/GlobalErrorHandler/AppNotFoundRoute";
import { PortalRouteGuard } from "./components/GlobalErrorHandler/PortalRouteGuard";
import { ADMIN_ACCESS_ROLES } from "./utils/roleUtils";
import { EditTrainingRecords } from "./pages/TrainingRecords/EditTrainingRecords/EditTrainingRecords";
import { TrainingRecords } from "./pages/TrainingRecords/TrainingRecords";
import { AppBootstrap } from "./components/AppBootstrap/AppBootstrap";
import { AuthRoute } from "./authentication/components/AuthRoute";
import { AssesmentReport } from "./pages/Analytics/RunReports/AssesmentReport/AssesmentReport";
import { LinkExpired } from "./authentication/LinkExpired/LinkExpired";
import { BulkUserActions } from "./pages/UserManagement/BulkUserActions/BulkUserActions";

const Routes = () => {
  return (
    <Router>
      <GlobalErrorHandler />
      <RoutesWrapper>
        <Route element={<AppBootstrap />}>
          {/* Routes accessible to all authenticated users */}
          <Route
            element={
              <ProtectedRoute
                allowedRoles={[
                  ...ADMIN_ACCESS_ROLES,
                  Roles.END_USER,
                  Roles.FILE_HUB,
                  Roles.REPORT_VIEWER,
                  Roles.EMAIL_MNG,
                  Roles.USER_MNG,
                  Roles.USER_IMPORT_JOB,
                  Roles.PORTAL_CONFIG,
                  Roles.ASSIGNMENT_MNG,
                ]}
              />
            }
          >
            <Route element={<PortalPageVisibilityGuard />}>
              <Route path="/" element={<Navigate to="/home" replace />} />
              <Route path="/home" element={<Home />}>
                <Route path="assignments/*" element={<AssignmentsView />} />
              </Route>
              <Route path="/courses" element={<Courses />} />
              <Route path="/certificates" element={<Certificates />} />
              <Route
                path="/assignment/:assignmentId"
                element={<HomeAssignment />}
              />
              <Route path="/profile" element={<MyProfile />} />
            </Route>
          </Route>

          {/* Portal configuration route - accessible to all ADMIN roles and REPORT_VIEWER, FILE_HUB, EMAIL_MANAGEMENT, USER_MANAGEMENT, PORTAL_CONFIG, ASSIGNMENT_MANAGEMENT */}
          <Route
            element={
              <ProtectedRoute
                allowedRoles={[
                  ...ADMIN_ACCESS_ROLES,
                  Roles.REPORT_VIEWER,
                  Roles.FILE_HUB,
                  Roles.EMAIL_MNG,
                  Roles.USER_MNG,
                  Roles.USER_IMPORT_JOB,
                  Roles.PORTAL_CONFIG,
                  Roles.ASSIGNMENT_MNG,
                ]}
              />
            }
          >
            <Route
              path="/portal-configuration"
              element={<PortalConfiguration />}
            />
          </Route>

          {/* Portal configuration routes - accessible to all ADMIN roles and PORTAL_CONFIG */}
          <Route
            element={
              <ProtectedRoute
                allowedRoles={[...ADMIN_ACCESS_ROLES, Roles.PORTAL_CONFIG]}
              />
            }
          >
            <Route
              path="/portal-configuration/:portalUid"
              element={<PortalRouteGuard />}
            >
              {/* Portal configuration sub-routes */}
              <Route index element={<PortalSettings />} />
              <Route path="portal-settings" element={<PortalSettings />} />
              <Route path="user-attributes" element={<UserAttributes />} />
              <Route path="portal-design" element={<PortalDesign />} />
              <Route path="portal-pages" element={<PortalPages />} />
              <Route path="group-cards" element={<PortalGroups />} />
              <Route
                path="portal-translation"
                element={<PortalTranslation />}
              />
              <Route
                path="portal-translation/find-and-replace"
                element={<FindAndReplace />}
              />
            </Route>
          </Route>

          {/* File hub routes - accessible to all ADMIN roles and FILE_HUB */}
          <Route
            element={
              <ProtectedRoute
                allowedRoles={[...ADMIN_ACCESS_ROLES, Roles.FILE_HUB]}
              />
            }
          >
            <Route
              path="/portal-configuration/:portalUid"
              element={<PortalRouteGuard />}
            >
              <Route path="file-hub" element={<FileHub />} />
            </Route>
          </Route>

          {/* User management routes - accessible to all ADMIN roles and USER_MANAGEMENT  */}
          <Route
            element={
              <ProtectedRoute
                allowedRoles={[...ADMIN_ACCESS_ROLES, Roles.USER_MNG]}
              />
            }
          >
            <Route
              path="/portal-configuration/:portalUid"
              element={<PortalRouteGuard />}
            >
              <Route path="users" element={<Users />} />
              <Route path="users/add-new-user" element={<AddNewUser />} />
              <Route
                path="users/reports/user-report"
                element={<UserReport />}
              />
              <Route path="users/email-send" element={<EmailSend />} />
              <Route
                path="users/edit-attributes"
                element={<EditAttributes />}
              />
              <Route
                path="users/manage-assignments"
                element={<ManageAssignments />}
              />
              <Route path="users/assignments/add" element={<AddAssignment />} />
              <Route
                path="users/assignments/:id/edit"
                element={<EditAssignment />}
              />
              <Route
                path="users/edit-training-records"
                element={<EditTrainingRecords />}
              />
            </Route>
          </Route>

          {/* Bulk user actions - accessible to all ADMIN roles, USER_MANAGEMENT and USER_IMPORT_JOB */}
          <Route
            element={
              <ProtectedRoute
                allowedRoles={[
                  ...ADMIN_ACCESS_ROLES,
                  Roles.USER_MNG,
                  Roles.USER_IMPORT_JOB,
                ]}
              />
            }
          >
            <Route
              path="/portal-configuration/:portalUid"
              element={<PortalRouteGuard />}
            >
              <Route path="bulk-user-actions" element={<BulkUserActions />} />
            </Route>
          </Route>

          {/* Training record detail - accessible to all ADMIN roles only */}
          <Route element={<ProtectedRoute allowedRoles={ADMIN_ACCESS_ROLES} />}>
            <Route
              path="/portal-configuration/:portalUid"
              element={<PortalRouteGuard />}
            >
              <Route
                path="users/edit-training-records/:id"
                element={<TrainingRecords />}
              />
            </Route>
          </Route>

          {/* Assignments routes - accessible to all ADMIN roles and ASSIGNMENT_MANAGEMENT */}
          <Route
            element={
              <ProtectedRoute
                allowedRoles={[...ADMIN_ACCESS_ROLES, Roles.ASSIGNMENT_MNG]}
              />
            }
          >
            <Route
              path="/portal-configuration/:portalUid"
              element={<PortalRouteGuard />}
            >
              <Route
                path="assignment-search"
                element={<AssignmentManagement />}
              />
              <Route
                path="assignment-search/group-cards"
                element={<PortalGroups />}
              />
              <Route
                path="assignment-uploads"
                element={<AssignmentUploads />}
              />
              <Route
                path="assignment-uploads/:assignmentUid/edit"
                element={<AssignmentUploads />}
              />
              <Route
                path="assignment-search/:assignmentUid/rules"
                element={<AssignmentRules />}
              />
              <Route
                path="assignment-search/:assignmentUid/rules/:ruleUid"
                element={<AddEditRule />}
              />
            </Route>
          </Route>

          {/* Email management routes - accessible to all ADMIN roles and EMAIL_MANAGEMENT */}
          <Route
            element={
              <ProtectedRoute
                allowedRoles={[...ADMIN_ACCESS_ROLES, Roles.EMAIL_MNG]}
              />
            }
          >
            <Route
              path="/portal-configuration/:portalUid"
              element={<PortalRouteGuard />}
            >
              <Route path="email-templates" element={<EmailTemplates />} />
              <Route
                path="email-templates/add-email-template"
                element={<EmailTemplateEditor />}
              />
              <Route
                path="email-templates/email-template/:id"
                element={<EmailTemplateEditor />}
              />
            </Route>
          </Route>

          {/* Analytics routes - accessible to all ADMIN roles and REPORT_VIEWER */}
          <Route
            element={
              <ProtectedRoute
                allowedRoles={[...ADMIN_ACCESS_ROLES, Roles.REPORT_VIEWER]}
              />
            }
          >
            <Route
              path="/portal-configuration/:portalUid"
              element={<PortalRouteGuard />}
            >
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="reports" element={<Reports />} />
              <Route
                path="reports/new-report"
                element={<NewReportTemplate />}
              />
              <Route path="reports/user-report" element={<UserReport />} />
              <Route
                path="reports/assignment-activity-report"
                element={<AssignmentActivityReport />}
              />
              <Route
                path="reports/user-activity-report"
                element={<UserActivityReport />}
              />
              <Route
                path="reports/assesment-report"
                element={<AssesmentReport />}
              />
            </Route>
          </Route>

          {/* Guest routes - redirect to home if already logged in */}
          <Route element={<GuestRoute />}>
            <Route element={<AuthRoute />}>
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/new-password" element={<NewPassword />} />
              <Route path="/link-expired" element={<LinkExpired />} />
            </Route>
          </Route>
          {/* If user tries to enter any route that doesn't exist, show 404 error */}
          <Route path="*" element={<AppNotFoundRoute />} />
        </Route>

        <Route path="/loading" element={<LoadingPage />} />
      </RoutesWrapper>
    </Router>
  );
};

export default Routes;
